import originalAxios from "axios";
import { axios } from "@/app/services/axios";
import { authConfig } from "./config";
import { IAuthenticatedUser, ILoginCredentials } from "./types";

/**
 * Bootstrap Sanctum CSRF protection.
 * MUST be called before any mutating SPA request.
 * Spec ref: AC-1 — GET /sanctum/csrf-cookie → 204, XSRF-TOKEN cookie set.
 */
export const initializeCsrfProtection = async (): Promise<void> => {
    await axios.get(authConfig.sanctumCsrfUrl);
};

/**
 * SPA login flow.
 * 1. Bootstraps CSRF cookie.
 * 2. POSTs credentials to backend at POST /api/v1/login.
 * 3. Returns ONLY the user object — the `token` field from the response body
 *    is explicitly discarded. The session cookie is the authoritative credential
 *    for SPA callers. Storing the token in JS is prohibited.
 *
 * Spec ref: AC-2 — SPA security gate (token discard), AC-3 (session cookie flow).
 */
export const login = async (credentials: ILoginCredentials): Promise<IAuthenticatedUser> => {
    await initializeCsrfProtection();

    const response = await axios.post<{ token: string; user: IAuthenticatedUser }>(
        "login",
        credentials,
    );

    // AC-2 security gate: discard the token — session cookie is authoritative for SPA.
    // Do NOT return response.data directly. Return only the user.
    return response.data.user;
};

/**
 * Fetch the currently authenticated user.
 * Used for session rehydration on page load.
 *
 * Returns null when no session is active (401 from the server).
 * The 401 is caught here and NOT propagated to the global interceptor, which
 * would otherwise trigger a hard redirect before React state settles.
 *
 * Spec ref: AC-4 — GET /api/v1/me returns user id, name, email.
 *           AC-11 — unauthenticated returns 401 (handled as null here).
 */
export const getCurrentUser = async (): Promise<IAuthenticatedUser | null> => {
    try {
        const response = await axios.get<{ data: IAuthenticatedUser }>("me", {
            skipAuthRedirect: true,
        });
        return response.data.data;
    } catch (error) {
        if (originalAxios.isAxiosError(error) && error.response?.status === 401) {
            return null;
        }
        throw error;
    }
};

/**
 * Logout the current session.
 * Invalidates the session server-side; subsequent requests with stale cookie return 401.
 * Spec ref: AC-5.
 */
export const logout = async (): Promise<void> => {
    await axios.post("logout");
};
